import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { ArrowLeft, Edit, Shield } from "react-feather";
import DataTable from "../../../../components/common/DataTable";
import { confirmDelete } from "../../../../components/common/ConfirmDialog";
import { useCanCreateProducts, useCanEditProducts } from "../useProductPermissions";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { paths } from "../../../../routes/paths";
import {
  useGetProductGroupsQuery,
  useCreateProductGroupMutation,
  useUpdateProductGroupMutation,
  useDeleteProductGroupMutation,
} from "../productsApi";
import ListExportToolbar from "../../../stock/components/list/ListExportToolbar";
import BulkWarrantyModal from "../components/BulkWarrantyModal";

// Feeds the Product Group dropdown on the Product form. Branch-wise, like
// Product itself (handoff §9.8) - each branch keeps its own group tree
// (every branch has its own "Master" root, groupId 1), so creating/editing a
// group needs a branch picked first. The list endpoint has no working
// branchId filter server-side (confirmed live - same 244 rows regardless),
// so this filters client-side against the header branch picker.
const ProductGroupsPage = () => {
  const canCreate = useCanCreateProducts();
  const canEdit = useCanEditProducts();
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const { data: allGroups = [], isLoading } = useGetProductGroupsQuery();
  const [createGroup, { isLoading: isCreating }] = useCreateProductGroupMutation();
  const [updateGroup, { isLoading: isUpdating }] = useUpdateProductGroupMutation();
  const [deleteGroup] = useDeleteProductGroupMutation();

  const [mode, setMode] = useState("new"); // "new" | "view" | "edit"
  const [selectedKey, setSelectedKey] = useState(null); // `${branchId}-${groupId}`
  const [branchId, setBranchId] = useState("");
  const [groupName, setGroupName] = useState("");
  const [groupUnder, setGroupUnder] = useState("");
  const [search, setSearch] = useState("");
  const [bulkWarrantyOpen, setBulkWarrantyOpen] = useState(false);

  const records = (selectedBranchId
    ? allGroups.filter((g) => g.branchId === selectedBranchId)
    : allGroups
  ).filter((g) => !search.trim() || g.groupName.toLowerCase().includes(search.trim().toLowerCase()));

  const selectedRecord = useMemo(
    () => allGroups.find((g) => `${g.branchId}-${g.groupId}` === selectedKey) || null,
    [allGroups, selectedKey]
  );

  const branchGroups = allGroups.filter((g) => g.branchId === Number(branchId));

  const scopeBranchCode = selectedBranchId
    ? availableBranches.find((b) => b.branchId === selectedBranchId)?.branchCode
    : null;
  const exportRows = [...records].sort((a, b) => a.groupName.localeCompare(b.groupName));

  useEffect(() => {
    if (mode === "new") {
      setGroupName("");
      const defaultBranch = selectedBranchId || availableBranches[0]?.branchId || "";
      setBranchId(defaultBranch ? String(defaultBranch) : "");
      const master = allGroups.find((g) => g.branchId === Number(defaultBranch) && g.groupName?.trim().toLowerCase() === "master");
      setGroupUnder(master ? String(master.groupId) : "");
    } else if (selectedRecord) {
      setBranchId(String(selectedRecord.branchId));
      setGroupName(selectedRecord.groupName);
      setGroupUnder(String(selectedRecord.groupUnder ?? ""));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, selectedRecord]);

  const isReadOnly = mode === "view";
  const canWrite = mode === "new" ? canCreate : canEdit;

  const select = (key) => {
    setSelectedKey(key);
    setMode("view");
  };
  const startEdit = () => setMode("edit");
  const startNew = () => {
    setSelectedKey(null);
    setMode("new");
  };

  const handleSave = async () => {
    if (!branchId) {
      toast.error("Branch is required");
      return;
    }
    if (!groupName.trim()) {
      toast.error("Name is required");
      return;
    }
    const body = {
      branchId: Number(branchId),
      groupName: groupName.trim(),
      groupUnder: groupUnder === "" ? null : Number(groupUnder),
    };
    try {
      if (mode === "edit" && selectedRecord) {
        await updateGroup({ branchId: selectedRecord.branchId, groupId: selectedRecord.groupId, ...body }).unwrap();
      } else {
        await createGroup(body).unwrap();
      }
      toast.success("Product group saved");
      startNew();
    } catch (error) {
      toast.error(error?.message || "Could not save product group");
    }
  };

  const handleDelete = async () => {
    if (!selectedRecord) return;
    const confirmed = await confirmDelete({ title: `Delete ${selectedRecord.groupName}?` });
    if (!confirmed) return;
    try {
      await deleteGroup({ branchId: selectedRecord.branchId, groupId: selectedRecord.groupId }).unwrap();
      toast.success("Product group deleted");
      startNew();
    } catch (error) {
      toast.error(error?.message || "Could not delete product group");
    }
  };

  const columns = [
    { title: "Group", dataIndex: "groupName" },
    { title: "Branch", dataIndex: "branchCode", render: (_, r) => availableBranches.find((b) => b.branchId === r.branchId)?.branchCode || r.branchId },
    {
      title: "",
      dataIndex: "actions",
      width: 60,
      render: (_, record) => (
        <button type="button" className="btn btn-link p-1" onClick={() => select(`${record.branchId}-${record.groupId}`)}>
          <Edit size={14} />
        </button>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <Link to={paths.products} className="d-inline-flex align-items-center mb-2">
                <ArrowLeft size={16} className="me-1" /> Back to Products
              </Link>
              <h4>Product Groups</h4>
              <h6>Category tree feeding the Product master's Group field</h6>
            </div>
          </div>
          {/* Also offered on the Products list, but a group-wide warranty setting is
              what someone standing on this page is most likely looking for. */}
          {canEdit && (
            <div className="page-btn">
              <button type="button" className="btn btn-outline-primary" onClick={() => setBulkWarrantyOpen(true)}>
                <Shield className="me-2" size={16} />
                Set Warranty by Group
              </button>
            </div>
          )}
        </div>

        {!canCreate && !canEdit && (
          <div className="alert alert-info">
            You have read-only access. Managing product groups requires Products.Create/.Edit.
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <h5 className="mb-3">Details</h5>
            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">Branch</label>
                <select
                  className="form-control"
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                  disabled={isReadOnly || mode === "edit" || !canWrite}
                >
                  <option value="">--Select--</option>
                  {availableBranches.map((b) => (
                    <option key={b.branchId} value={b.branchId}>{b.branchCode}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-8 mb-3">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  readOnly={isReadOnly || !canWrite}
                  disabled={isReadOnly || !canWrite}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Under Group</label>
                <select
                  className="form-control"
                  value={groupUnder}
                  onChange={(e) => setGroupUnder(e.target.value)}
                  disabled={isReadOnly || !canWrite || !branchId}
                >
                  <option value="">-- None (top-level) --</option>
                  {branchGroups.map((g) => (
                    <option key={g.groupId} value={g.groupId}>{g.groupName}</option>
                  ))}
                </select>
              </div>
            </div>
            {(canCreate || canEdit) && (
              <div className="d-flex gap-2">
                {mode !== "view" && canWrite && (
                  <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isCreating || isUpdating}>
                    Save
                  </button>
                )}
                {mode === "view" && canEdit && (
                  <button type="button" className="btn btn-outline-primary" onClick={startEdit}>
                    Edit
                  </button>
                )}
                <button type="button" className="btn btn-secondary" onClick={startNew}>
                  Clear
                </button>
                {mode === "view" && canEdit && (
                  <button type="button" className="btn btn-outline-danger" onClick={handleDelete}>
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Search group name"
                    className="form-control form-control-sm formsearch"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
              <ListExportToolbar
                title="Product Groups"
                subtitleLines={[scopeBranchCode ? `Branch: ${scopeBranchCode}` : "All Branches"]}
                branchCode={scopeBranchCode}
                columns={[
                  { header: "Group Name", value: (r) => r.groupName },
                  {
                    header: "Branch",
                    value: (r) => availableBranches.find((b) => b.branchId === r.branchId)?.branchCode || r.branchId,
                  },
                ]}
                rows={exportRows}
                fileName="ProductGroups"
              />
            </div>
            <div className="table-responsive">
              <DataTable
                columns={columns}
                dataSource={records}
                rowKey={(r) => `${r.branchId}-${r.groupId}`}
                loading={isLoading}
              />
            </div>
          </div>
        </div>
      </div>

      <BulkWarrantyModal
        open={bulkWarrantyOpen}
        onClose={() => setBulkWarrantyOpen(false)}
        branchId={selectedBranchId || branchId}
      />
    </div>
  );
};

export default ProductGroupsPage;
